import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Shield, Users, UserX, UserCheck, Save,
  Key, Clock, ChevronDown, ChevronUp, Phone, Mail, AlertTriangle, Eye, EyeOff,
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { apiClient } from '../lib/apiClient';
import { RoleBadge } from '../components/Badge';
import { isAdminRole } from '../lib/utils';
import Modal from '../components/Modal';
import ClickUpSettingsCard from '../components/ClickUpSettingsCard';
import type { User, UserRole } from '../types';

// ── UserForm ─────────────────────────────────────────────────────────────────

function UserForm({
  initial,
  currentUser,
  onSave,
  onCancel,
}: {
  initial?: Partial<User>;
  currentUser: User;
  onSave: (data: Omit<User, 'id'>) => void;
  onCancel: () => void;
}) {
  const isEditingSelf = initial?.id !== undefined && initial.id === currentUser.id;

  const [form, setForm] = useState({
    name: initial?.name ?? '',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    role: (initial?.role ?? 'pm') as UserRole,
    initials: initial?.initials ?? '',
  });

  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';

  const handleNameChange = (name: string) => {
    const parts = name.trim().split(' ');
    const auto = parts.map((p) => p[0]?.toUpperCase() ?? '').join('').slice(0, 2);
    setForm((f) => ({ ...f, name, initials: f.initials || auto }));
  };

  const roles: { value: UserRole; label: string; desc: string }[] = [
    { value: 'admin', label: 'Admin', desc: 'Full project access, manage users' },
    { value: 'sales', label: 'Salesperson', desc: 'Clients, projects, documents' },
    { value: 'pm', label: 'Project Manager', desc: 'Stages, invoices, chat' },
    { value: 'sales_pm', label: 'Sales & PM', desc: 'Both roles combined' },
    { value: 'viewer', label: 'Viewer', desc: 'Read-only access to assigned projects' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Full Name *</label>
          <input
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            className={inputCls}
            placeholder="Jane Smith"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Initials</label>
          <input
            value={form.initials}
            onChange={(e) => setForm((f) => ({ ...f, initials: e.target.value.toUpperCase().slice(0, 2) }))}
            className={inputCls}
            placeholder="JS"
            maxLength={2}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Email *</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className={inputCls}
            placeholder="jane@vdisain.ee"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Phone</label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            className={inputCls}
            placeholder="+372 5xxx xxxx"
          />
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Role *</label>
        <select
          value={form.role}
          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}
          className={inputCls}
        >
          {roles.map((r) => (
            <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>
          ))}
        </select>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
          Cancel
        </button>
        <button
          onClick={() => form.name.trim() && form.email.trim() && onSave(form)}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium"
        >
          <Save size={13} /> Save
        </button>
      </div>
    </div>
  );
}

// ── Audit log row ─────────────────────────────────────────────────────────────

interface AuditEvent {
  id: string;
  action: string;
  userId: string | null;
  ipAddress: string | null;
  createdAt: string;
  metadata: Record<string, unknown> | null;
  user?: { id: string; name: string; email: string } | null;
}

function actionLabel(action: string, meta: Record<string, unknown> | null) {
  switch (action) {
    case 'user.created': return 'Created user';
    case 'user.updated':
      if (meta?.oldRole && meta?.newRole) return `Changed role: ${meta.oldRole} → ${meta.newRole}`;
      return 'Updated user';
    case 'user.disabled': return 'Disabled user';
    case 'user.enabled': return 'Enabled user';
    case 'user.password_reset': return 'Reset password';
    default: return action;
  }
}

function actionColor(action: string) {
  if (action === 'user.created') return 'text-green-600';
  if (action === 'user.disabled') return 'text-red-500';
  if (action === 'user.password_reset') return 'text-amber-600';
  return 'text-gray-600';
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Admin() {
  const currentUser = useStore((s) => s.currentUser);
  const users = useStore((s) => s.users);
  const addUser = useStore((s) => s.addUser);
  const updateUser = useStore((s) => s.updateUser);
  const deleteUser = useStore((s) => s.deleteUser);
  const enableUser = useStore((s) => s.enableUser);
  const resetPassword = useStore((s) => s.resetPassword);
  const projects = useStore((s) => s.projects);
  const clients = useStore((s) => s.clients);
  const navigate = useNavigate();

  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [showDisabled, setShowDisabled] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetShowPassword, setResetShowPassword] = useState(false);
  const [resetResult, setResetResult] = useState<{ password: string; name: string } | null>(null);
  const [resetting, setResetting] = useState(false);

  const isAdmin = isAdminRole(currentUser?.role);

  useEffect(() => {
    if (showAudit && isAdmin && auditEvents.length === 0) {
      setAuditLoading(true);
      apiClient.get<{ events: AuditEvent[] }>('/users/audit/recent')
        .then((r) => setAuditEvents(r.events))
        .catch(() => {})
        .finally(() => setAuditLoading(false));
    }
  }, [showAudit, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-md mx-auto mt-16 text-center">
        <Shield size={48} className="text-gray-200 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">Access Restricted</h2>
        <p className="text-gray-400 text-sm mb-4">This page is only available to administrators.</p>
        <button onClick={() => navigate('/')} className="px-4 py-2 bg-brand-500 text-white rounded-lg text-sm hover:bg-brand-600">
          Back to Dashboard
        </button>
      </div>
    );
  }

  const ROLE_ORDER: Record<string, number> = { super_admin: -1, admin: 0, sales: 1, sales_pm: 2, pm: 3, viewer: 4 };
  const activeUsers   = users.filter((u) => !u.disabledAt);
  const disabledUsers = users.filter((u) => !!u.disabledAt);
  const visibleUsers  = showDisabled ? users : activeUsers;
  const sortedUsers   = [...visibleUsers].sort(
    (a, b) => (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99) || a.name.localeCompare(b.name),
  );

  const activeAdmins = activeUsers.filter((u) => isAdminRole(u.role));
  const projectsForUser = (userId: string) =>
    projects.filter((p) => p.salesOwnerId === userId || p.projectManagerId === userId).length;

  // Super admins can't be changed by anyone — the server enforces this too.
  const canEditUser = (user: User) => user.role !== 'super_admin';
  const canDisableUser = (user: User) => {
    if (user.role === 'super_admin') return false;
    if (user.disabledAt) return false;
    if (user.id === currentUser?.id) return false;
    if (user.role === 'admin' && activeAdmins.length <= 1) return false;
    return true;
  };
  const canResetPassword = (user: User) => user.role !== 'super_admin';

  const avatarColor = (role: string) => {
    if (role === 'super_admin') return 'bg-gray-900';
    if (role === 'admin') return 'bg-rose-500';
    if (role === 'sales') return 'bg-blue-500';
    if (role === 'sales_pm') return 'bg-indigo-500';
    if (role === 'viewer') return 'bg-gray-400';
    return 'bg-teal-500';
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const r = await resetPassword(resetTarget.id, resetPasswordValue.trim() || undefined);
      setResetResult({ password: r.tempPassword, name: resetTarget.name });
      setResetTarget(null);
      setResetPasswordValue('');
    } catch {
      // silently handled — server error shown via console
    } finally {
      setResetting(false);
    }
  };

  const refreshAudit = () => {
    if (!isAdmin) return;
    setAuditLoading(true);
    apiClient.get<{ events: AuditEvent[] }>('/users/audit/recent')
      .then((r) => setAuditEvents(r.events))
      .catch(() => {})
      .finally(() => setAuditLoading(false));
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {activeUsers.length} active
            {disabledUsers.length > 0 && isAdmin && ` · ${disabledUsers.length} disabled`}
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600"
        >
          <Plus size={15} /> Add User
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <p className="text-xs text-gray-400 mb-1">Total Clients</p>
          <p className="text-3xl font-bold text-gray-900">{clients.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <p className="text-xs text-gray-400 mb-1">Total Projects</p>
          <p className="text-3xl font-bold text-gray-900">{projects.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <p className="text-xs text-gray-400 mb-1">Active Projects</p>
          <p className="text-3xl font-bold text-gray-900">{projects.filter((p) => p.status === 'active').length}</p>
        </div>
      </div>

      <ClickUpSettingsCard />

      {/* Users table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-gray-400" />
            <h2 className="font-semibold text-gray-900 text-sm">Team Members</h2>
          </div>
          {disabledUsers.length > 0 && (
            <button
              onClick={() => setShowDisabled((v) => !v)}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                showDisabled ? 'bg-gray-100 border-gray-200 text-gray-700' : 'border-gray-200 text-gray-400 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {showDisabled ? 'Hide disabled' : `Show disabled (${disabledUsers.length})`}
            </button>
          )}
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-50 bg-gray-50">
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Contact</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Role</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Projects</th>
              <th className="px-5 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sortedUsers.map((user) => {
              const disabled = !!user.disabledAt;
              const isLastAdmin = user.role === 'admin' && activeAdmins.length <= 1;
              return (
                <tr
                  key={user.id}
                  className={`transition-colors group ${disabled ? 'opacity-50 bg-gray-50/50' : 'hover:bg-gray-50/50'}`}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${
                        disabled ? 'bg-gray-400' : avatarColor(user.role)
                      }`}>
                        {user.initials}
                      </div>
                      <div>
                        <div className="font-medium text-sm text-gray-900 flex items-center gap-2">
                          {user.name}
                          {disabled && <span className="text-xs text-gray-400 font-normal bg-gray-100 px-1.5 py-0.5 rounded">disabled</span>}
                          {user.id === currentUser?.id && <span className="text-xs text-brand-500 font-normal">you</span>}
                        </div>
                        {isLastAdmin && !disabled && (
                          <div className="text-xs text-amber-500 flex items-center gap-1">
                            <AlertTriangle size={10} /> last admin
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Mail size={11} className="text-gray-300 shrink-0" />
                        {user.email}
                      </div>
                      {user.phone && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-400">
                          <Phone size={11} className="text-gray-300 shrink-0" />
                          {user.phone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    {disabled ? <span className="text-xs text-gray-400">—</span> : <RoleBadge role={user.role} />}
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-gray-600">{projectsForUser(user.id)}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!disabled && canEditUser(user) && (
                        <button
                          onClick={() => setEditing(user)}
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                          title="Edit user"
                        >
                          <Pencil size={13} />
                        </button>
                      )}
                      {!disabled && canResetPassword(user) && (
                        <button
                          onClick={() => setResetTarget(user)}
                          className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                          title="Reset password"
                        >
                          <Key size={13} />
                        </button>
                      )}
                      {disabled && (
                        <button
                          onClick={() => { if (confirm(`Re-enable ${user.name}?`)) enableUser(user.id); }}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                          title="Re-enable user"
                        >
                          <UserCheck size={13} />
                        </button>
                      )}
                      {!disabled && canDisableUser(user) && (
                        <button
                          onClick={() => { if (confirm(`Disable ${user.name}? They will lose access immediately.`)) deleteUser(user.id); }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Disable user"
                        >
                          <UserX size={13} />
                        </button>
                      )}
                      {isLastAdmin && !disabled && (
                        <span className="text-xs text-gray-300 px-1" title="Cannot disable last admin">
                          <Shield size={13} />
                        </span>
                      )}
                      {user.role === 'super_admin' && (
                        <span className="flex items-center gap-1 text-xs text-gray-400 px-1" title="Super admin — cannot be changed or removed">
                          <Shield size={13} /> Protected
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Audit log */}
      {(
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-6">
          <button
            onClick={() => setShowAudit((v) => !v)}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-gray-400" />
              <h2 className="font-semibold text-gray-900 text-sm">Audit Log</h2>
              <span className="text-xs text-gray-400">— recent user & role changes</span>
            </div>
            {showAudit ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
          </button>
          {showAudit && (
            <div className="border-t border-gray-50">
              <div className="px-5 py-2 flex justify-end">
                <button onClick={refreshAudit} className="text-xs text-gray-400 hover:text-gray-600">Refresh</button>
              </div>
              {auditLoading ? (
                <div className="px-5 py-8 text-center text-sm text-gray-400">Loading…</div>
              ) : auditEvents.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-gray-400">No audit events found.</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-50 bg-gray-50">
                      <th className="px-5 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">When</th>
                      <th className="px-5 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Action</th>
                      <th className="px-5 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">By</th>
                      <th className="px-5 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">IP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {auditEvents.map((ev) => (
                      <tr key={ev.id} className="hover:bg-gray-50/50">
                        <td className="px-5 py-3 text-xs text-gray-400 whitespace-nowrap">
                          {new Date(ev.createdAt).toLocaleString('et-EE', { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                        <td className={`px-5 py-3 text-xs font-medium ${actionColor(ev.action)}`}>
                          {actionLabel(ev.action, ev.metadata)}
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-500">
                          {ev.user?.name ?? ev.user?.email ?? ev.userId ?? '—'}
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-400 font-mono">{ev.ipAddress ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Role reference */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="font-semibold text-gray-900 text-sm mb-4">Role Permissions</h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { role: 'Admin', color: 'bg-rose-100 text-rose-700', perms: ['All Sales & PM permissions', 'Manage users', 'View all projects', 'Edit any record', 'Enable / disable users', 'Reset passwords', 'Cannot disable last admin'] },
            { role: 'Salesperson', color: 'bg-blue-100 text-blue-700', perms: ['Create & manage clients', 'Create projects', 'Upload documents', 'Review extracted data', 'Reply to PM questions'] },
            { role: 'Project Manager', color: 'bg-teal-100 text-teal-700', perms: ['View project timeline', 'Update stage progress', 'Mark invoice status', 'Ask questions in chat', 'View documents'] },
            { role: 'Sales & PM', color: 'bg-indigo-100 text-indigo-700', perms: ['All Sales permissions', 'All PM permissions', 'Appears in both dropdowns', 'Sees own projects as both roles'] },
            { role: 'Viewer', color: 'bg-gray-100 text-gray-600', perms: ['Read-only access', 'Can view assigned projects', 'Cannot create or edit anything', 'Cannot upload documents', 'No chat participation'] },
          ].map(({ role, color, perms }) => (
            <div key={role} className="border border-gray-100 rounded-xl p-4">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium mb-3 ${color}`}>{role}</span>
              <ul className="space-y-1.5">
                {perms.map((perm) => (
                  <li key={perm} className="flex items-start gap-1.5 text-xs text-gray-500">
                    <span className="text-green-400 mt-0.5">✓</span> {perm}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Add user modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Team Member">
        {currentUser && (
          <UserForm
            currentUser={currentUser}
            onSave={(data) => { addUser(data); setShowAdd(false); }}
            onCancel={() => setShowAdd(false)}
          />
        )}
      </Modal>

      {/* Edit user modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit User">
        {editing && currentUser && (
          <UserForm
            initial={editing}
            currentUser={currentUser}
            onSave={(data) => { updateUser(editing.id, data); setEditing(null); }}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>

      {/* Reset password modal */}
      <Modal open={!!resetTarget} onClose={() => { setResetTarget(null); setResetPasswordValue(''); }} title="Set Password">
        {resetTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Setting a new password for <strong>{resetTarget.name}</strong>. Their active sessions will be revoked and they will be required to set up 2FA on next login.
            </p>
            <div>
              <label className="text-xs text-gray-500 block mb-1">New password</label>
              <div className="relative">
                <input
                  type={resetShowPassword ? 'text' : 'password'}
                  value={resetPasswordValue}
                  onChange={(e) => setResetPasswordValue(e.target.value)}
                  placeholder="Leave blank to auto-generate"
                  className="w-full px-3 py-2 pr-9 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setResetShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {resetShowPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">If left blank, a random password will be generated and shown to you after saving.</p>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setResetTarget(null); setResetPasswordValue(''); }} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resetting}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium disabled:opacity-60"
              >
                <Key size={13} /> {resetting ? 'Saving…' : 'Set Password'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Password set result modal */}
      <Modal open={!!resetResult} onClose={() => setResetResult(null)} title="Password Set">
        {resetResult && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Password for <strong>{resetResult.name}</strong> has been set. Share it securely — it will not be shown again.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between gap-3">
              <code className="text-sm font-mono text-gray-900 break-all">{resetResult.password}</code>
              <button
                onClick={() => navigator.clipboard.writeText(resetResult.password)}
                className="text-xs text-brand-500 hover:text-brand-600 shrink-0 font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-gray-400">The user's 2FA enrollment has also been cleared. They will re-enroll on first login.</p>
            <div className="flex justify-end">
              <button onClick={() => setResetResult(null)} className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium">
                Done
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
